// release-verification.ts
//
// P7 §14 test 45: VERIFYING phase — health 200 KHÔNG đủ để coi app chạy
// đúng. Đối chiếu HAI bằng chứng độc lập:
//   1. artifact rollout — candidate SHA thực sự đang chạy (không phải bản
//      cũ còn cache/health check trả 200 từ instance chưa cập nhật).
//   2. business smoke — recipe verification-profile đã duyệt chạy qua
//      verification-recipe-runner.ts (P6), KHÔNG tự bịa health-check riêng.
import type {
  RunVerificationRecipeInput,
  RunVerificationRecipeResult,
} from "./verification-recipe-runner.js";
import { runVerificationRecipe } from "./verification-recipe-runner.js";

export interface ArtifactRolloutCheck {
  expectedCandidateSha: string;
  /** Injected — v1 reads whatever the recipe's rollout-check step reports. */
  observedRunningSha: string | undefined;
}

export interface VerifyingInput {
  /**
   * H14 (2026-09-29): optional. v1 has no probe for "which SHA is actually
   * running"; the wiring used to fill this with `observed = expected`, a check
   * that could never fail. Absent ⇒ rollout is reported `not_observed`, never
   * as verified. Present ⇒ compared exactly as before (test 45).
   */
  artifactRollout?: ArtifactRolloutCheck;
  smokeRecipe: RunVerificationRecipeInput["recipe"];
  env: Record<string, string | undefined>;
  timeoutMs: number;
  /** Phase 04a (review L5): known-values che OUTPUT smoke — không cấp env. */
  secretValues?: readonly string[];
  /** Optional extra signal some caller might have (e.g. raw health probe) —
   * kept ONLY to prove it alone never suffices (test 45). */
  healthCheckStatus?: number;
}

export type RolloutObservation = "matched" | "not_observed";

export type VerifyingResult =
  | { status: "verified"; smoke: RunVerificationRecipeResult; rollout: RolloutObservation }
  | { status: "rollout_mismatch"; reason: string }
  | { status: "smoke_failed"; reason: string; smoke: RunVerificationRecipeResult };

/**
 * Test 45: even with healthCheckStatus===200, a mismatched rollout SHA or a
 * failing business-smoke recipe still fails verification. Health 200 is
 * accepted as auxiliary context only, never as the deciding signal.
 */
export async function runVerifyingChecks(input: VerifyingInput): Promise<VerifyingResult> {
  const { artifactRollout } = input;
  if (
    artifactRollout !== undefined &&
    artifactRollout.observedRunningSha !== artifactRollout.expectedCandidateSha
  ) {
    return {
      status: "rollout_mismatch",
      reason:
        `Candidate SHA kỳ vọng "${artifactRollout.expectedCandidateSha}" nhưng artifact đang ` +
        `chạy là "${artifactRollout.observedRunningSha ?? "(không xác định)"}" — health 200 ` +
        "không thay được việc đúng artifact đang chạy.",
    };
  }

  const smoke = await runVerificationRecipe({
    recipe: input.smokeRecipe,
    env: input.env,
    timeoutMs: input.timeoutMs,
    secretValues: [...(input.secretValues ?? [])],
  });

  if (smoke.exitCode !== 0 || smoke.timedOut) {
    return {
      status: "smoke_failed",
      reason: `Business smoke recipe thất bại (exitCode=${smoke.exitCode}, timedOut=${smoke.timedOut}).`,
      smoke,
    };
  }

  return {
    status: "verified",
    smoke,
    rollout: artifactRollout === undefined ? "not_observed" : "matched",
  };
}
